/**
 * Margin Report Tooltips Hook — adapted for @bill/ui/DataGrid
 *
 * Provides cell renderers (row → ReactNode) with tooltips that explain
 * each value on hover : formula breakdown for derived rows, source
 * explanation for base rows.
 *
 * @see US-25d.12 - Composant MarginReportTable
 */

import { useCallback, useMemo } from 'react';
import { Tooltip } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import type { MarginReportGridRow } from '@bstock/shared';
import { DERIVED_ROWS, ROW_EXPLANATIONS, type DerivedRowConfig } from '../config/margin-report.config';

interface UseMarginReportTooltipsParams {
  rowLookup: Map<string, MarginReportGridRow>;
  ventesNettes: number;
  currency: string;
  calculateDerivedValue: (config: DerivedRowConfig, field: 'theoValue' | 'reelValue') => number | null;
}

export function useMarginReportTooltips({
  rowLookup,
  ventesNettes,
  currency,
  calculateDerivedValue,
}: UseMarginReportTooltipsParams) {
  // Generate tooltip content for any row
  const getValueTooltip = useCallback(
    (rowId: string, field: 'theo' | 'reel', currentValue: number | null): React.ReactNode | null => {
      const derivedConfig = DERIVED_ROWS[rowId];
      if (derivedConfig && derivedConfig.fields.includes(field)) {
        const valueField = field === 'theo' ? 'theoValue' : 'reelValue';
        const values = derivedConfig.sources.map((id) => {
          const row = rowLookup.get(id);
          return { id, label: row?.label || id, value: row ? row[valueField] : null };
        });

        let formulaText = '';
        let calculationText = '';

        switch (derivedConfig.type) {
          case 'subtract':
            formulaText = `${values[0]?.label} - ${values[1]?.label}`;
            calculationText = `${formatCentimes(values[0]?.value || 0, currency)} - ${formatCentimes(values[1]?.value || 0, currency)}`;
            break;
          case 'add':
            formulaText = values.map((v) => v.label).join(' + ');
            calculationText = values.map((v) => formatCentimes(v.value || 0, currency)).join(' + ');
            break;
          case 'subtract_multiple':
            formulaText = `${values[0]?.label} - (${values.slice(1).map((v) => v.label).join(' + ')})`;
            calculationText = `${formatCentimes(values[0]?.value || 0, currency)} - (${values.slice(1).map((v) => formatCentimes(Math.abs(v.value || 0), currency)).join(' + ')})`;
            break;
          case 'signed': {
            const signs = derivedConfig.signs ?? values.map(() => 1);
            formulaText = values.map((v, i) => {
              const prefix = i === 0 ? (signs[i] === -1 ? '- ' : '') : signs[i] === -1 ? ' - ' : ' + ';
              return `${prefix}${v.label}`;
            }).join('');
            calculationText = values.map((v, i) => {
              const prefix = i === 0 ? (signs[i] === -1 ? '- ' : '') : signs[i] === -1 ? ' - ' : ' + ';
              return `${prefix}${formatCentimes(v.value || 0, currency)}`;
            }).join('');
            break;
          }
        }

        const resultValue = calculateDerivedValue(derivedConfig, valueField);

        return (
          <div className="space-y-1">
            <div className="font-medium text-white/90">{formulaText}</div>
            <div className="text-white/70 font-mono text-xs">{calculationText}</div>
            <div className="text-white font-medium border-t border-white/20 pt-1 mt-1">
              = {formatCentimes(resultValue || 0, currency)}
            </div>
          </div>
        );
      }

      const explanation = ROW_EXPLANATIONS[rowId];
      const fieldExplanation = explanation?.[field];

      if (fieldExplanation && currentValue !== null) {
        return (
          <div className="space-y-1">
            <div className="text-white/80 text-xs">{fieldExplanation}</div>
            <div className="text-white font-medium">{formatCentimes(currentValue, currency)}</div>
          </div>
        );
      }

      return null;
    },
    [rowLookup, calculateDerivedValue, currency]
  );

  /**
   * Create a cell renderer for a value column (theoValue / reelValue).
   * Receives the enriched row (derived values already pre-computed).
   */
  const createValueCellRender = useCallback(
    (field: 'theo' | 'reel', getValue: (row: MarginReportGridRow) => number | null) => {
      return (row: MarginReportGridRow): React.ReactNode => {
        const value = getValue(row);
        const formatted = value === null || value === undefined ? '—' : formatCentimes(value, currency);
        if (value === null || value === undefined) return <span>—</span>;

        const tooltipContent = getValueTooltip(row.id, field, value);
        if (!tooltipContent) return <span>{formatted}</span>;

        return (
          <Tooltip content={tooltipContent} position="top" wide portal>
            <span className="cursor-help">{formatted}</span>
          </Tooltip>
        );
      };
    },
    [getValueTooltip, currency]
  );

  /**
   * Create a cell renderer for a percentage column (theoPct / reelPct).
   */
  const createPctCellRender = useCallback(
    (_field: 'theo' | 'reel', getSourceValue: (row: MarginReportGridRow) => number | null) => {
      return (row: MarginReportGridRow): React.ReactNode => {
        const sourceValue = getSourceValue(row);
        const pctValue =
          sourceValue === null || sourceValue === undefined
            ? null
            : ventesNettes <= 0
              ? 0
              : Math.round((sourceValue / ventesNettes) * 10000) / 100;

        const formatted = pctValue === null || pctValue === undefined ? '—' : `${pctValue.toFixed(2)}%`;
        if (pctValue === null || pctValue === undefined) return <span>—</span>;

        const refRow = rowLookup.get('ca-net');
        const refLabel = refRow?.label || 'Ventes Nettes';

        const tooltipContent = (
          <div className="space-y-1">
            <div className="text-white/80 text-xs">Valeur / {refLabel} × 100</div>
            <div className="text-white/70 font-mono text-xs">
              {formatCentimes(sourceValue || 0, currency)} / {formatCentimes(ventesNettes, currency)} × 100
            </div>
            <div className="text-white font-medium border-t border-white/20 pt-1 mt-1">= {formatted}</div>
          </div>
        );

        return (
          <Tooltip content={tooltipContent} position="top" wide portal>
            <span className="cursor-help">{formatted}</span>
          </Tooltip>
        );
      };
    },
    [rowLookup, ventesNettes, currency]
  );

  // Pre-built cell renderers
  const theoValueCell = useMemo(() => createValueCellRender('theo', (r) => r.theoValue), [createValueCellRender]);
  const reelValueCell = useMemo(() => createValueCellRender('reel', (r) => r.reelValue), [createValueCellRender]);
  const theoPctCell = useMemo(() => createPctCellRender('theo', (r) => r.theoValue), [createPctCellRender]);
  const reelPctCell = useMemo(() => createPctCellRender('reel', (r) => r.reelValue), [createPctCellRender]);

  return { theoValueCell, reelValueCell, theoPctCell, reelPctCell };
}
